import {
  Grid,
  Typography,
  Paper,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Devices as DevicesIcon,
  AccountBalance as AccountBalanceIcon,
  StickyNote2 as StickyNote2Icon,
  ContentCopy as ContentCopyIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import type { Application } from '../../../types/application';
import STATUS_COLORS from '../../../../../constants/statusColors';
import { formatDate } from '../utils';

interface Props {
  application: Application;
  onCopyAccount: () => void;
  onCopyAddress: () => void;
}

const DetailInfoTab = ({ application, onCopyAccount, onCopyAddress }: Props) => {
  return (
    <Grid container spacing={3}>
      {/* 👤 신청자 정보 */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <PersonIcon sx={{ mr: 1, fontSize: 18, color: '#1976d2' }} />
          신청자 정보
        </Typography>
        <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e0e7ef' }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                성명
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {application.user_name}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                연락처
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {application.phone_number}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                신청일
              </Typography>
              <Typography variant="body2">
                {formatDate(application.created_at)}
              </Typography>
            </Grid>
            
            {/* 🏠 주소 정보 */}
            {application.pickup_address && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  수거 주소
                </Typography>
                <Box display="flex" alignItems="center">
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mr: 1,
                      lineHeight: 1.4,
                      wordBreak: 'break-all'
                    }}
                  >
                    {application.pickup_address}
                    {application.pickup_address_detail && (
                      <Box component="span" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.875rem' }}>
                        {application.pickup_address_detail}
                      </Box>
                    )}
                  </Typography>
                  <Tooltip title="주소 복사" arrow>
                    <IconButton 
                      size="small" 
                      onClick={onCopyAddress} 
                      sx={{ 
                        color: '#1976d2',
                        alignSelf: 'flex-start',
                        mt: 0.25
                      }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>

      {/* 📱 기기 정보 */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <DevicesIcon sx={{ mr: 1, fontSize: 18, color: '#1976d2' }} />
          기기 정보 ({application.devices?.length || 0}개)
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {application.devices?.map((device, index) => (
            <Chip
              key={index}
              label={`${device.deviceType} ${device.deviceModel}`}
              sx={{
                bgcolor: '#e3f2fd',
                color: '#1565c0',
                fontWeight: 600,
                '& .MuiChip-label': { px: 1.5 },
              }}
            />
          )) || (
            <Typography variant="body2" color="text.secondary">
              기기 정보가 없습니다
            </Typography>
          )}
        </Box>
      </Grid>

      {/* 🏦 계좌 정보 상세 */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <AccountBalanceIcon sx={{ mr: 1, fontSize: 18, color: '#1976d2' }} />
          계좌 정보 상세
        </Typography>
        <Paper elevation={0} sx={{ p: 2, bgcolor: '#f0f7ff', borderRadius: 2, border: '1px solid #bbdefb' }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                은행명
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {application.bank_name || '정보없음'}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                예금주
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {application.account_holder || application.bank_owner || '정보없음'}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                계좌번호
              </Typography>
              <Box display="flex" alignItems="center">
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontFamily: 'monospace', letterSpacing: '0.5px', mr: 1 }}
                >
                  {application.bank_account || '정보없음'}
                </Typography>
                {application.bank_account && (
                  <Tooltip title="계좌번호 복사" arrow>
                    <IconButton size="small" onClick={onCopyAccount} sx={{ color: '#1976d2' }}>
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* 📋 신청 상세 */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <StickyNote2Icon sx={{ mr: 1, fontSize: 18, color: '#1976d2' }} />
          신청 상세 정보
        </Typography>
        <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e0e7ef' }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                신청번호
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                {application.application_number || application.id?.slice(-8) || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                현재 상태
              </Typography>
              <Chip
                label={application.status}
                size="small"
                sx={{
                  bgcolor: STATUS_COLORS[application.status] || '#757575',
                  color: '#fff',
                  fontWeight: 600,
                }}
              />
            </Grid>
            {application.updated_at && (
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block' }}>
                  최종 수정일
                </Typography>
                <Typography variant="body2">
                  {new Date(application.updated_at.seconds * 1000).toLocaleString('ko-KR')}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default DetailInfoTab; 